/**
 * Runtime mode detection.
 *
 * Embedded mode = running inside Unity's WebView (loaded from file:// or
 * with the bridge receiver already installed by native code).
 *
 * All feature checks go through this module so components never need to
 * know about build flags or URL sniffing.
 *
 * ┌───────────────┬────────────┬──────────┬─────────────────────────────────┐
 * │ Capability     │ Standalone │ Embedded │ Notes                           │
 * ├───────────────┼────────────┼──────────┼─────────────────────────────────┤
 * │ bridge         │     ✗      │    ✓     │ Unity WebView data transport    │
 * │ fileIO         │     ✓      │    ✗     │ Standalone-only file APIs (drag-drop/download .previs); embedded whole-view drop targets are bridge-gated, see key-patterns.md │
 * │ onlineReview   │     ✓      │    ✓     │ Supabase realtime sessions      │
 * │ comments       │     ✓      │    ✓     │ Comments, approvals, doodles    │
 * │ sharing        │     ✓      │    ✓     │ Share / export HTML             │
 * │ aiGeneration   │     ✓      │    ✓     │ Blocked in review by readOnly   │
 * │ embeddedAudio  │     ✗      │    ✓     │ Audio playback via Unity native │
 * │ embeddedRec    │     ✗      │    ✓     │ Mic capture via Unity native    │
 * │ avatarCustom   │     ✗      │    ✓     │ Avatar customizer panel (3D rendered in Unity) │
 * └───────────────┴────────────┴──────────┴─────────────────────────────────┘
 *
 * Standalone and embedded modes are functionally identical — they only
 * differ in data transport (bridge vs file I/O). AI generation and
 * editing are blocked in review mode by readOnly guards,
 * not by capability flags.
 */

const isEmbedded: boolean =
  typeof window !== 'undefined' &&
  (window.location.protocol === 'file:' ||
   '__previsRecv' in window ||
   new URLSearchParams(window.location.search).has('embedded'));

// App.tsx only mounts SceneBoardPage (the shell with the top bar + hamburger menu) on the
// page=scene-board route; other embedded routes that survive behind kill-switches or a direct
// MCP ShowTimeline navigation render a bare TimelineView with no shell around it at all. Anything
// that targets shell-only chrome (e.g. Quick Orient's hamburger-menu coach step) has to gate on
// this rather than assuming the shell is always there just because capabilities.bridge is true.
const hasSceneBoardShell: boolean =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('page') === 'scene-board';

// In embedded mode, suppress the browser context menu so the WebViews
// feel like native UI rather than a web page (no Reload, Autofill, etc.).
if (isEmbedded) {
  document.addEventListener('contextmenu', (e) => e.preventDefault());
}

export const capabilities = {
  /** Unity bridge available — data pushed from native side */
  bridge: isEmbedded,

  /** Can open / save .previs files via drag-drop + download */
  fileIO: !isEmbedded,

  /** Online review sessions (Supabase) */
  onlineReview: true,

  /** Comments and approvals UI */
  comments: true,

  /** Share for review / export HTML */
  sharing: true,

  /** AI image enhancement, video, TTS */
  aiGeneration: true,

  /** Audio playback routed through Unity native AudioSource (embedded only) */
  embeddedAudio: isEmbedded,

  /** Audio recording routed through Unity native Microphone API (embedded only) */
  embeddedRecording: isEmbedded,

  /** Avatar customizer panel — Unity renders the 3D; WebView is UI-only (embedded only) */
  avatarCustomizer: isEmbedded,

  /** SceneBoardPage shell (top bar + hamburger menu) is mounted around TimelineView —
   *  true only on the page=scene-board route. False on older/kill-switched embedded routes
   *  and MCP's ShowTimeline, which can still land on a bare TimelineView with no shell. */
  sceneBoardShell: hasSceneBoardShell,

  /** "Show me where" auto-navigate add-on in Previs Assist help (M1 MCP).
   *  Disabled until the navigate UX is fleshed out. Flipping this to true also
   *  re-enables the previs_navigate tool server-side, because the flag is
   *  forwarded to the previs-assist edge function (which gates the tool + its
   *  system-prompt section on it). */
  assistShowMeNavigation: false,
};
